import tfLogo from '@assets/footer/tf.svg';
import wuLogo from '@assets/footer/wu.svg';
import { useGlobalDIContext } from '@context/GlobalDIContext';
import { TRANSACTION_ROUTES } from '@features/transaction/routes';
import { ArrowForward } from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';

// Styled components
const FooterBanner = styled(Box)(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: '#2d323e',
  isolation: 'isolate',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '&::before': {
    content: '""',
    position: 'absolute',
    height: '400px',
    width: '400px',
    borderRadius: '200px',
    backgroundColor: '#383e4d',
    right: '-10%',
    zIndex: -1,
  },
}));

const BasicLink = styled('a')(({ theme }) => ({
  textDecoration: 'none',
}));

const FooterBannerButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100%',
  alignSelf: 'stretch',
  backgroundColor: '#383e4d',
  position: 'relative',
  padding: theme.spacing(2),
  color: '#fafafa',
  textDecoration: 'none',
  textTransform: 'none',
  transition: 'color 0.25s ease-in-out',
  '&:hover': {
    color: '#adadad',
    backgroundColor: '#383e4d',
  },
}));

const FooterSection = styled(Box)(({ theme }) => ({
  flex: '0 0 calc(100% - 1rem)',
  display: 'flex',
  marginTop: theme.spacing(2),
  flexDirection: 'column',
  [theme.breakpoints.up('md')]: {
    flex: '0 0 calc(50% - 1rem)',
  },
  [theme.breakpoints.up('lg')]: {
    flex: '0 0 calc(25% - 1rem)',
  },
  [theme.breakpoints.up('xl')]: {
    flex: '0 0 calc(20% - 1rem)',
  },
}));

const FooterRow = styled(Grid)(({ theme }) => ({
  gap: theme.spacing(1),
}));

const FooterBrandDivider = styled(Divider)(({ theme }) => ({
  width: '156px',
  height: '2px',
  borderRadius: '1px',
  backgroundColor: '#fafafa',
}));

const FooterList = styled(List)(({ theme }) => ({
  display: 'grid',
  gap: '0.25rem',
}));

const FooterHeading = styled(Typography)(({ theme }) => ({
  opacity: 0.45,
  fontSize: '0.75rem',
  fontWeight: 500,
  marginBottom: '0.75rem',
  textTransform: 'uppercase',
  color: '#adadad',
}));

const FooterText = styled(ListItemText)(({ theme }) => ({
  '& .MuiTypography-root': {
    fontSize: '0.875rem',
    color: '#adadad',
    transition: 'color 0.25s ease-in-out',
    '&:hover': {
      color: '#fafafa',
    },
  },
}));

const FooterHr = styled(Divider)(({ theme }) => ({
  backgroundColor: '#adadad',
  marginBlock: '1rem',
  opacity: 0.25,
  '&.right': {
    width: '20%',
    alignSelf: 'flex-end',
    marginRight: theme.spacing(3),
  },
}));

const WuLayoutFooter: React.FC = () => {
  const theme = useTheme();
  const { TranslationAdapter } = useGlobalDIContext();
  const { t } = TranslationAdapter();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: '#22262f',
        py: 5,
        [theme.breakpoints.up('md')]: {
          py: 10,
        },
      }}
    >
      <Container>
        {/* Banner Section */}
        <Grid container spacing={2} mb={5}>
          <Grid item xs={12}>
            <FooterBanner>
              <Box px={5} py={4}>
                <Typography variant="h5" component="h2" color="#ededed">
                  {t('footer_cta_title')}
                </Typography>
                <Typography variant="body1" mb={0} color="#adadad">
                  {t('footer_cta_subtitle')}
                </Typography>
              </Box>
              <FooterBannerButton href={TRANSACTION_ROUTES.sendMoney} endIcon={<ArrowForward />}>
                {t('footer_cta')}
              </FooterBannerButton>
            </FooterBanner>
          </Grid>
        </Grid>

        <Grid display="flex" flexDirection="column" gap={10}>
          {/* Main Footer Content */}
          <FooterRow container>
            {/* Logo Section */}
            <FooterSection>
              <BasicLink href={t('tf_home_link')}>
                <Box
                  component="img"
                  src={tfLogo}
                  alt="Tenfore logo"
                  height={40}
                  width={158}
                  mb={4}
                />
              </BasicLink>
              <FooterBrandDivider />
              <BasicLink href={t('wu_home_link')}>
                <Box
                  component="img"
                  src={wuLogo}
                  alt="Western union logo"
                  height={80}
                  width={165}
                />
              </BasicLink>
              <FooterHr className="right" />
              <Box display="flex" gap={3} alignItems="center">
                <FooterHeading>{t('footer_phone')}:</FooterHeading>
                <Box display="flex" flexDirection="column">
                  <Link href="tel:+381113334999" color="#adadad">
                    <FooterText primary="+381 11 333 4 999" />
                  </Link>
                  <Link href="tel:+381114004999" color="#adadad">
                    <FooterText primary="+381 11 400 4 999" />
                  </Link>
                </Box>
              </Box>
            </FooterSection>

            {/* Empty Section for XL screens */}
            <FooterSection sx={{ display: { xs: 'none', xl: 'flex' } }} />

            {/* Support Section */}
            <FooterSection>
              <FooterList disablePadding>
                <ListItem disablePadding>
                  <FooterHeading>{t('footer_support')}</FooterHeading>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_customer_support_url')} color="text.primary">
                    <FooterText primary={t('footer_customer_support')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_faq_url')} color="text.primary">
                    <FooterText primary={t('footer_faq')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('complaints_url')} color="text.primary">
                    <FooterText primary={t('complaints')} />
                  </BasicLink>
                </ListItem>
              </FooterList>
            </FooterSection>

            {/* About Us Section */}
            <FooterSection>
              <FooterList disablePadding>
                <ListItem disablePadding>
                  <FooterHeading>{t('footer_about_us')}</FooterHeading>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_about_WU_url')} color="text.primary">
                    <FooterText primary={t('footer_about_WU')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_about_tenfore_url')} color="text.primary">
                    <FooterText primary={t('footer_about_tenfore')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_our_service_url')} color="text.primary">
                    <FooterText primary={t('footer_our_service')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_nbs_licence_url')} color="text.primary">
                    <FooterText primary={t('footer_nbs_licence')} />
                  </BasicLink>
                </ListItem>
              </FooterList>
            </FooterSection>

            {/* Info Section */}
            <FooterSection>
              <FooterList disablePadding>
                <ListItem disablePadding>
                  <FooterHeading>{t('footer_info')}</FooterHeading>
                </ListItem>
                <ListItem disablePadding>
                  <Link href={TRANSACTION_ROUTES.sendMoney} color="text.primary">
                    <FooterText primary={t('sendMoneyWithWu')} />
                  </Link>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_protection_url')} color="text.primary">
                    <FooterText primary={t('footer_protection')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_ways_to_send_url')} color="text.primary">
                    <FooterText primary={t('footer_ways_to_send')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_WU_instructions_url')} color="text.primary">
                    <FooterText primary={t('footer_WU_instructions')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_registration_instructions_url')} color="text.primary">
                    <FooterText primary={t('footer_registration_instructions')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_track_transfer_url')} color="text.primary">
                    <FooterText primary={t('footer_track_transfer')} />
                  </BasicLink>
                </ListItem>
              </FooterList>
            </FooterSection>
          </FooterRow>

          {/* Bottom Links */}
          <FooterRow container>
            <FooterSection>
              <FooterHr />
              <FooterList disablePadding>
                <ListItem disablePadding>
                  <BasicLink
                    href={t('footer_privacy_policy_url')}
                    target="_blank"
                    color="text.primary"
                  >
                    <FooterText primary={t('footer_privacy_policy')} />
                  </BasicLink>
                </ListItem>
              </FooterList>
            </FooterSection>

            <FooterSection>
              <FooterHr />
              <FooterList disablePadding>
                <ListItem disablePadding>
                  <BasicLink
                    href={t('footer_terms_of_use_url')}
                    target="_blank"
                    color="text.primary"
                  >
                    <FooterText primary={t('footer_terms_of_use')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink
                    href={t('footer_new_terms_of_use_url')}
                    target="_blank"
                    color="text.primary"
                  >
                    <FooterText primary={t('footer_new_terms_of_use')} />
                  </BasicLink>
                </ListItem>
              </FooterList>
            </FooterSection>

            <FooterSection>
              <FooterHr />
              <FooterList disablePadding>
                <ListItem disablePadding>
                  <BasicLink
                    href={t('footer_general_terms_url')}
                    target="_blank"
                    color="text.primary"
                  >
                    <FooterText primary={t('footer_general_terms')} />
                  </BasicLink>
                </ListItem>
                <ListItem disablePadding>
                  <BasicLink href={t('footer_pricing_url')} target="_blank" color="text.primary">
                    <FooterText primary={t('footer_pricing')} />
                  </BasicLink>
                </ListItem>
              </FooterList>
            </FooterSection>

            <FooterSection>
              <FooterHr />
              <FooterList disablePadding>
                <ListItem disablePadding>
                  <Link href={t('footer_data_collection_url')} target="_blank" color="text.primary">
                    <FooterText primary={t('footer_data_collection')} />
                  </Link>
                </ListItem>
              </FooterList>
            </FooterSection>

            <FooterSection />
          </FooterRow>
        </Grid>

        {/* Copyright */}
        <Grid container mt={5}>
          <Grid item xs={12}>
            <FooterHeading>© {new Date().getFullYear()} — Copyright</FooterHeading>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default WuLayoutFooter;
